import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
} from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material'
import { contentGroupsAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'

// Normalize list responses to an array regardless of envelope shape
const toList = (data, key) => {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.[key])) return data[key]
  if (Array.isArray(data?.data)) return data.data
  return []
}

const EMPTY_FORM = {
  code: '',
  name: '',
  description: '',
  is_active: true,
}

function ContentGroups() {
  const { hasRole } = useAuth()
  const canEdit = hasRole(['system_admin', 'administrator', 'clinical_designer'])
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showInactive, setShowInactive] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingGroup, setEditingGroup] = useState(null)
  const [formData, setFormData] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    fetchGroups()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive])

  const fetchGroups = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await contentGroupsAPI.getAll(showInactive)
      setGroups(toList(response.data, 'content_groups'))
    } catch (err) {
      console.error('Error fetching content groups:', err)
      setError('Failed to load content groups. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleOpenDialog = (group = null) => {
    if (group) {
      setEditingGroup(group)
      setFormData({
        code: group.code || '',
        name: group.name || '',
        description: group.description || '',
        is_active: group.is_active !== false,
      })
    } else {
      setEditingGroup(null)
      setFormData(EMPTY_FORM)
    }
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditingGroup(null)
    setFormData(EMPTY_FORM)
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)
      if (editingGroup) {
        await contentGroupsAPI.update(editingGroup.id, formData)
      } else {
        await contentGroupsAPI.create(formData)
      }
      handleCloseDialog()
      fetchGroups()
    } catch (err) {
      console.error('Error saving content group:', err)
      setError(err.response?.data?.message || 'Failed to save content group. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const columns = [
    {
      field: 'code',
      headerName: 'Code',
      width: 160,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      minWidth: 250,
    },
    {
      field: 'is_active',
      headerName: 'Active',
      width: 80,
      renderCell: (params) => (
        <Typography
          variant="body2"
          fontSize="0.8125rem"
          sx={{ color: params.value ? '#16a34a' : '#dc2626' }}
        >
          {params.value ? 'Active' : 'Inactive'}
        </Typography>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 110,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" fontSize="0.8125rem">
          {params.value ? new Date(params.value).toLocaleDateString() : '—'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title={canEdit ? 'Edit' : 'View'}>
          <IconButton size="small" onClick={() => handleOpenDialog(params.row)}>
            {canEdit ? <EditIcon fontSize="small" /> : <ViewIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      ),
    },
  ]

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h5">Content Groups</Typography>
          <Typography variant="body2" color="text.secondary">
            Group pathway content (resources, information sources, QOL concerns) into named
            collections that can be attached to pathways.
          </Typography>
        </Box>
        {canEdit && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            New Group
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
            }
            label={<Typography variant="body2">Show inactive</Typography>}
          />
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            {groups.length} groups
          </Typography>
        </Box>
      </Paper>

      <Paper>
        <DataGrid
          rows={groups}
          columns={columns}
          pageSize={15}
          rowsPerPageOptions={[15, 25, 50]}
          autoHeight
          disableSelectionOnClick
          getRowId={(row) => row.id}
          onRowDoubleClick={(params) => handleOpenDialog(params.row)}
        />
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingGroup ? (canEdit ? 'Edit Content Group' : 'View Content Group') : 'Create Content Group'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Code"
              value={formData.code}
              onChange={(e) =>
                setFormData({ ...formData, code: e.target.value.toLowerCase().replace(/\s+/g, '_') })
              }
              margin="normal"
              required
              disabled={!canEdit || !!editingGroup}
              helperText={
                editingGroup
                  ? 'Code cannot be changed after creation'
                  : 'Unique identifier (e.g., bc_pathway_content)'
              }
              inputProps={{ style: { fontFamily: 'monospace' } }}
            />
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
              disabled={!canEdit}
              helperText="Display name for this content group"
            />
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              multiline
              rows={3}
              disabled={!canEdit}
              helperText="Brief description of what content this group contains"
            />
            {editingGroup && (
              <Box sx={{ mt: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      disabled={!canEdit}
                    />
                  }
                  label="Active"
                />
                {!formData.is_active && (
                  <Typography variant="caption" color="warning.main" display="block">
                    Warning: deactivating this group removes its content from every pathway.
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{canEdit ? 'Cancel' : 'Close'}</Button>
          {canEdit && (
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={saving || !formData.code || !formData.name}
            >
              {saving ? 'Saving...' : editingGroup ? 'Update' : 'Create'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ContentGroups
